import type { AxiosError } from 'axios';

export type RequestError = Error & {
  body?: unknown;
  status?: number;
  url?: string;
};

export type LegacyResultError = {
  error: true;
  respBody: unknown;
  status?: number;
};

export const DOWNSTREAM_CONNECTION_ERROR_MESSAGE =
  'Unable to connect to the server. Please check your connection and try again.';

export function getErrorStatus(error: unknown): number | undefined {
  return (error as AxiosError)?.response?.status ?? (error as RequestError)?.status;
}

export function getErrorBody(error: unknown): unknown {
  return (error as AxiosError)?.response?.data
    ?? (error as RequestError)?.body
    ?? (error as LegacyResultError)?.respBody;
}

export function getErrorUrl(error: unknown): string | undefined {
  return (error as AxiosError)?.config?.url ?? (error as RequestError)?.url;
}

export function createRequestError(error: unknown): RequestError {
  if (error instanceof Error && ('status' in error || 'body' in error || 'url' in error)) {
    return error as RequestError;
  }

  const status = getErrorStatus(error);
  const requestError = new Error(
    (error as AxiosError)?.message || `Request failed${status ? `: ${status}` : ''}`
  ) as RequestError;
  requestError.status = status;
  requestError.body = getErrorBody(error);
  requestError.url = getErrorUrl(error);
  return requestError;
}

export function createLegacyResultError(error: unknown): LegacyResultError {
  return {
    error: true,
    respBody: getErrorBody(error),
    status: getErrorStatus(error),
  };
}

export function throwIfLegacyResultError<T>(result: T, fallback = 'Request failed.'): T {
  if (!result || typeof result !== 'object' || (result as { error?: unknown }).error !== true) {
    return result;
  }

  const body =
    (result as { respBody?: unknown }).respBody ??
    (result as { body?: unknown }).body ??
    (result as { message?: unknown }).message ??
    result;
  const status = Number((result as { status?: unknown }).status);
  const requestError = new Error(fallback) as RequestError;
  requestError.body = body;
  requestError.status = Number.isFinite(status) ? status : undefined;
  throw requestError;
}

export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred.'): string {
  const body = getErrorBody(error);

  if (typeof body === 'string' && body.trim()) {
    return body;
  }

  if (body && typeof body === 'object') {
    const candidate =
      (body as { message?: unknown }).message ||
      (body as { Message?: unknown }).Message ||
      (body as { error?: unknown }).error ||
      (body as { detail?: unknown }).detail ||
      (body as { title?: unknown }).title;

    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    const status = getErrorStatus(error);
    if (status === undefined || error.message !== `Request failed with status code ${status}`) {
      return error.message;
    }
  }

  return fallback;
}

export const SSO_REAUTH_REQUIRED_CODE = 'SSO_REAUTH_REQUIRED';
export const SSO_TEMPORARILY_UNAVAILABLE_CODE = 'SSO_TEMPORARILY_UNAVAILABLE';

const getErrorCode = (error: unknown): unknown => {
  const body = getErrorBody(error);
  return body && typeof body === 'object' ? (body as { code?: unknown }).code : undefined;
};

// The server found no usable stored Metrc sign-in for the request, so the user has to sign in with MCA again.
export function isSsoReauthRequired(error: unknown): boolean {
  return getErrorStatus(error) === 409 && getErrorCode(error) === SSO_REAUTH_REQUIRED_CODE;
}

// Keycloak could not be reached to refresh any stored sign-in; signing in again would not help.
export function isSsoTemporarilyUnavailable(error: unknown): boolean {
  return getErrorStatus(error) === 503 && getErrorCode(error) === SSO_TEMPORARILY_UNAVAILABLE_CODE;
}

export type SsoReauthContact = { name: string; mcaEmail?: string | null };

export type SsoReauthDetails = {
  message: string;
  // True when the user has an MCA account and can sign in themselves.
  canSignIn: boolean;
  organizationId: number | null;
  marketId: number | null;
  mcaFacilityId: number | null;
  // RetailID users with an MCA account to ask, for users who cannot sign in.
  contacts: SsoReauthContact[];
};

const toId = (value: unknown): number | null => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export function getSsoReauthDetails(error: unknown): SsoReauthDetails | null {
  if (!isSsoReauthRequired(error)) return null;
  const body = getErrorBody(error) as Record<string, unknown>;
  const contacts = Array.isArray(body.contacts)
    ? body.contacts
      .filter((contact): contact is Record<string, unknown> => Boolean(contact) && typeof contact === 'object')
      .map((contact) => ({
        name: typeof contact.name === 'string' ? contact.name : '',
        mcaEmail: typeof contact.mcaEmail === 'string' ? contact.mcaEmail : null,
      }))
      .filter((contact) => contact.name || contact.mcaEmail)
    : [];
  return {
    message: getErrorMessage(error, 'Metrc sign-in is required.'),
    canSignIn: body.canSignIn === true,
    organizationId: toId(body.organizationId),
    marketId: toId(body.marketId),
    mcaFacilityId: toId(body.mcaFacilityId),
    contacts,
  };
}

// Per-item results (for example bulk COA relink) carry the same SSO body as the 409/503 responses.
export function ssoErrorFromItemResult(item: unknown): RequestError | null {
  const code = item && typeof item === 'object' ? (item as { code?: unknown }).code : undefined;
  const status = code === SSO_REAUTH_REQUIRED_CODE ? 409 : code === SSO_TEMPORARILY_UNAVAILABLE_CODE ? 503 : null;
  if (!status) return null;
  const error = new Error(String((item as { message?: unknown }).message || 'Metrc sign-in failed.')) as RequestError;
  error.status = status;
  error.body = item;
  return error;
}

export function getEndpointErrorMessage(error: unknown, fallback: string): string {
  return getErrorStatus(error) === 502
    ? DOWNSTREAM_CONNECTION_ERROR_MESSAGE
    : getErrorMessage(error, fallback);
}

export function getSafeEndpointMessage(error: unknown, fallback: string): string {
  return getEndpointErrorMessage({
    status: getErrorStatus(error),
    body: getErrorBody(error),
  }, fallback);
}
